import { GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { PageSizes } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import {
  PaginatedTableProps,
  usePaginatedDataTableQuery,
} from '@/components/tables/DataTable/hooks/usePaginatedDataTableQuery';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { useReportError } from '@/hooks/useReportError';
import { CreateTeamModal } from '@/modules/teams/TeamModal/CreateTeamModal';
import { EditTeamModal } from '@/modules/teams/TeamModal/EditTeamModal';
import {
  mapDataToRows,
  useTeamTableColumns,
} from '@/pages/admin/AdminTeamsPage/AdminTeamsPage.utils';

import { useGetAdminTeamsQuery } from './graphql/AdminTeams.generated';

type Row = ReturnType<typeof mapDataToRows>[number];

export interface Props {
  rows: Row[];
  loading: boolean;
  paginatedTableProps: PaginatedTableProps;
}

const slots = {
  noRowsOverlay: () => <TableEmptyState text="No teams" />,
};

function AdminTeamsPageInner({ rows, loading, paginatedTableProps }: Props) {
  const columns = useTeamTableColumns();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [teamId, setTeamId] = useState<null | string>(null);

  const handleAddNewTeamClick = () => {
    setIsModalOpen(true);
    setTeamId(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTeamId(null);
  };

  return (
    <SubpageLayout
      heading="All teams"
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={handleAddNewTeamClick}
        >
          Add team
        </Button>
      }
    >
      <DataTableCard>
        <DataTable
          {...paginatedTableProps}
          alwaysShowPaginationFooter
          columns={columns}
          rows={rows}
          loading={loading}
          slots={slots}
          onRowClick={(row: GridRowParams<Row>) => {
            setIsModalOpen(true);
            setTeamId(row.row.id);
          }}
        />
      </DataTableCard>
      {isModalOpen &&
        (teamId ? (
          <EditTeamModal isOpen onClose={closeModal} teamId={teamId} />
        ) : (
          <CreateTeamModal isOpen onClose={closeModal} />
        ))}
    </SubpageLayout>
  );
}

export function AdminTeamsPage() {
  const { showFeedback } = useFeedback();
  const [paginatedTableProps, { data, loading, error }] =
    usePaginatedDataTableQuery(useGetAdminTeamsQuery, {
      pageSize: PageSizes.Ten,
      fetchPolicy: 'cache-and-network',
      onError: () => {
        showFeedback(
          "We weren't able to load teams. Please refresh the page and try again."
        );
      },
    });

  useReportError('failed to load data for teams page', error);

  const rows = useMemo(() => mapDataToRows(data), [data]);

  return (
    <>
      <AdminTeamsPageInner
        rows={rows}
        loading={loading}
        paginatedTableProps={paginatedTableProps}
      />
    </>
  );
}
